import React from 'react'
import clsx from 'clsx'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast-v2'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { useAPISyncToEkin } from 'api/kinerja/penilik-pegawai/overview'

import type {
  StatusPerencanaan,
  StatusEkinSync,
} from 'types/kinerja-penilik-pegawai'

type SyncToEkinProps = {
  perencanaanStatus: StatusPerencanaan
  ekinSyncErrorMessage?: string
  ekinSyncStatus?: StatusEkinSync
  isReadOnly?: boolean
  isError?: boolean
}

export const SyncToEkin: React.FC<SyncToEkinProps> = ({
  perencanaanStatus,
  ekinSyncErrorMessage = '',
  ekinSyncStatus = 'INIT',
  isReadOnly = false,
  isError = false,
}) => {
  const isPelaksanaanState = ['DISETUJUI_PPK', 'PELAKSANAAN_DIMULAI'].includes(
    perencanaanStatus
  )

  // E-Kinerja Sync
  const isEkinSyncSuccess = ['RHK_CREATED', 'PERSETUJUAN_SUCCESS'].includes(
    ekinSyncStatus
  )
  const isEkinSyncFailed = [
    'RHK_FAILED',
    'SKP_FAILED',
    'PERSETUJUAN_FAILED',
  ].includes(ekinSyncStatus)
  const isEkinSynced = isEkinSyncSuccess || isEkinSyncFailed

  const { isLoading, mutateAsync } = useAPISyncToEkin()
  const { period } = usePeriodContext()
  const toast = useToast()

  const submit = async () => {
    try {
      await mutateAsync({ periodeId: period.id })
      toast({ message: 'Berhasil kirim data ke E-Kinerja!', type: 'success' })
    } catch (e) {
      toast({ message: 'Gagal kirim data ke E-Kinerja. Coba lagi.' })
    }
  }

  return (
    <SimpleCard
      className={clsx(
        'p-4 space-y-2 border max-w-96',
        isError && '!bg-surface-neutral-default',
        !isError &&
          !isEkinSynced &&
          '!bg-surface-informational-subdued border-blue-40',
        isEkinSyncSuccess && '!border-green-50 !bg-surface-success-subdued',
        isEkinSyncFailed && '!border-red-40 !bg-surface-critical-subdued'
      )}
    >
      <div className="flex space-x-2">
        <div className="max-w-64">
          <Text variant="heading-sm">
            Kirim data Perencanaan (SKP) ke E-Kinerja
          </Text>
        </div>

        {isPelaksanaanState && (
          <div className="text-right">
            {isEkinSyncSuccess && (
              <div className="w-24">
                <Icon data-testid="sync-to-ekin-success-icon" color="success">
                  check_circle_outline
                </Icon>
              </div>
            )}

            {isEkinSyncFailed && (
              <Button
                data-testid="sync-to-ekin-button"
                disabled={isLoading}
                isLoading={isLoading}
                className="flex-1 !py-2 space-x-1"
                size="sm"
                {...(!isReadOnly && { onClick: submit })}
              >
                <Icon color="inverse" fontSize="sm">
                  share
                </Icon>
                <span>Kirim</span>
              </Button>
            )}
          </div>
        )}
      </div>

      {isError && <Badge>Terjadi kendala sistem. Mohon coba lagi nanti.</Badge>}

      {!isError && (
        <>
          {isEkinSyncSuccess && (
            <Badge variant="success">
              Berhasil dan sudah tampil di E-Kinerja
            </Badge>
          )}

          {isEkinSyncFailed && (
            <>
              <Badge variant="critical">Gagal</Badge>
              {Boolean(ekinSyncErrorMessage) && (
                <Text variant="body-sm">Catatan: {ekinSyncErrorMessage}</Text>
              )}
            </>
          )}

          {!isEkinSynced && (
            <>
              {isPelaksanaanState ? (
                <Badge variant="informational">Sedang dikirim</Badge>
              ) : (
                <Badge variant="neutral">
                  Selesaikan Perencanaan terlebih dahulu
                </Badge>
              )}
            </>
          )}
        </>
      )}
    </SimpleCard>
  )
}
